import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FadeLoader from "react-spinners/FadeLoader";
import "./SelectPricingPlan.css";
import "./PricingPlanList.css";
import ValidationMessages from "../utils/validationMessages";
import { fetchAllParkingLotsForPricingAssignment } from "../../Services/parkinglotService.js";
import { fetchUsersWithValidEmpId } from "../../Services/userService.js";
import {
  buildEntityZipMap,
  buildLotToEntityMap,
  buildNickNameToEntityMap,
  enrichPropertiesWithEntityZip,
  filterAssignableProperties,
  flattenParkingLotRecords,
  getEntityAddressZipCode,
  getEntityRegistrationId,
  getParkingLotId,
  getPropertyStatus,
  getPropertyZipCode,
  isPricingPlanAssignable,
  mergePropertyRecords,
  normalizePropertyForPricingAssignment,
  propertyMatchesZipCode,
  resolvePropertyRecordForAssignment,
} from "../../utils/pricingPlanEligibility.js";

const PricingPlanList = ({ onEditPlan, updateTrigger, setLoader }) => {
    const [rowsData, setRowsData] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

    // Delete Dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [deletingPlan, setDeletingPlan] = useState(false);

    // Assign Properties Dialog
    const [assignPropertiesPopup, setassignPropertiesPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [assignType, setAssignType] = useState("zip");
    const [selectedZipCodes, setSelectedZipCodes] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [filteredCities, setFilteredCities] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [allZipCodes, setAllZipCodes] = useState([]);
    const [rawProperties, setRawProperties] = useState([]);
    const [allProperties, setAllProperties] = useState([]);
    const [states, setstates] = useState([]);
    const [cities, setcities] = useState([]);
    const [alreadyAssignedProperties, setAlreadyAssignedProperties] = useState([]);
    const [assignError, setAssignError] = useState("");
    const [entityZipByEntityId, setEntityZipByEntityId] = useState({});
    const [lookupLots, setLookupLots] = useState([]);
    const [removingProperty, setRemovingProperty] = useState(false);

    // View Properties Dialog
    const [openPropertiesPopup, setOpenPropertiesPopup] = useState(false);

    const hostPath = process.env.REACT_APP_API_BASE_URL;
    const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;
    const weekOrder = [1, 2, 3, 4, 5, 6, 0];

    const SELECT_ALL = {
        label: "Select All",
        value: "__ALL__"
    };

    const getOptionValue = (option) => {
        if (option === SELECT_ALL || option?.value === SELECT_ALL.value) return SELECT_ALL.value;
        return option?.value ?? option;
    };

    const getOptionLabel = (option) => {
        if (option === SELECT_ALL || option?.value === SELECT_ALL.value) return SELECT_ALL.label;
        return option?.label ?? String(option ?? "");
    };

    const readApiError = async (response, fallbackMessage) => {
        const text = await response.text().catch(() => "");
        if (!text) return fallbackMessage;

        try {
            const data = JSON.parse(text);
            return data?.message || data?.responseMessage || data?.error || text;
        } catch {
            return text;
        }
    };

    const fetchJsonOrThrow = async (url, options, fallbackMessage) => {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(await readApiError(response, fallbackMessage));
        }
        return response;
    };

    const dayMap = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday"
    };

    // ================= FETCH PRICING PLANS =================
    const fetchPricingPlans = async () => {
        setLoader(true);
        try {
            const response = await fetch(`${hostPath}/api/GetAllPricingPlans`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                }
            });

            const data = await response.json();

            const formattedData = await Promise.all(
                data.map(async (item) => {
                    try {
                        const countResponse = await fetch(
                            `${hostPath}/api/GetPropertyCountByPricingPlan`,
                            {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    "x-functions-key": hostHeader
                                },
                                body: JSON.stringify({ pricingPlanName: item.pricingPlanName })
                            }
                        );

                        const countData = await countResponse.json();

                        return {
                            ...item,
                            isDefault: Boolean(
                                item?.isDefault ??
                                item?.IsDefault ??
                                item?.is_default ??
                                item?.Default
                            ),
                            propertyCount: countData.propertyCount ?? 0
                        };
                    } catch (err) {
                        return {
                            ...item,
                            isDefault: Boolean(
                                item?.isDefault ??
                                item?.IsDefault ??
                                item?.is_default ??
                                item?.Default
                            ),
                            propertyCount: 0,
                            rulesCount: item.rules?.length ?? 0
                        };
                    }
                })
            );

            setRowsData(formattedData);
        } catch (error) {
            console.error("Error fetching pricing plans:", error);
            setRowsData([]);
        } finally {
            setLoader(false);
        }
    };

    // ✅ Fetch on mount AND when updateTrigger changes
    useEffect(() => {
        fetchPricingPlans();
    }, [updateTrigger]);

    // ================= HANDLERS =================

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRowForMenu(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowForMenu(null);
    };

    const handleDeletePlan = async (row) => {
    setPlanToDelete(row);
    try {
        setPlanToDelete({ ...row, propertyCount: row.propertyCount ?? 0 });
    } catch (err) {
        setPlanToDelete({ ...row, propertyCount: 0 });
    } finally {
        setOpenDeleteDialog(true);
        handleMenuClose();
    }
};

    const handleDeleteConfirm = async (type) => {
        if (type === "cancel") {
            if (deletingPlan) return;
            setOpenDeleteDialog(false);
            setPlanToDelete(null);
            return;
        }

        setDeletingPlan(true);
        setLoader(true);
        try {
            await fetchJsonOrThrow(`${hostPath}/api/DeletePricingPlan`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify({
                    id: planToDelete?.id,
                    pricingPlanID: planToDelete?.pricingPlanID
                })
            }, "Failed to delete pricing plan");

            await fetchPricingPlans(); // Refresh
            setOpenDeleteDialog(false);
            setPlanToDelete(null);
        } catch (error) {
            console.error("Error deleting pricing plan:", error);
            alert(error.message || "Failed to delete pricing plan");
        } finally {
            setDeletingPlan(false);
            setLoader(false);
        }
    };

    const handleAssignClick = async (row) => {
        setLoader(true);
        try {
            await fetchZipCodes();
            const assignedProps = await fetchPropertyDetails(row.pricingPlanName.trim());
            setAlreadyAssignedProperties(assignedProps);
            setSelectedRow(row);
            setAssignType("zip");
            setSelectedZipCodes([]);
            setSelectedProperties([]);
            setAssignError("");
            setassignPropertiesPopup(true);
        } catch (error) {
            console.error("Failed to load assign data", error);
        } finally {
            setLoader(false);
        }
    };

    const enrichAssignedPropertiesForDisplay = async (properties) => {
        const [owners, entityLots, searchResponse, activeLotsResponse] = await Promise.all([
            fetchUsersWithValidEmpId().catch(() => []),
            fetchAllParkingLotsForPricingAssignment().catch(() => []),
            fetch(`${hostPath}/api/GetSearchProperty`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader,
                },
            }).catch(() => null),
            fetch(`${hostPath}/api/GetAllParkingLots?status=Active`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader,
                },
            }).catch(() => null),
        ]);

        const searchLots = searchResponse?.ok
            ? flattenParkingLotRecords(await searchResponse.json())
            : [];
        const activeLots = activeLotsResponse?.ok
            ? flattenParkingLotRecords(await activeLotsResponse.json()).map((lot) =>
                normalizePropertyForPricingAssignment(lot, { defaultStatus: "Active" })
              )
            : [];

        const lots = mergePropertyRecords(entityLots, searchLots, activeLots);
        const zipMap = buildEntityZipMap(owners);
        setEntityZipByEntityId(zipMap);
        setLookupLots(lots);

        const lotToEntityMap = buildLotToEntityMap(lots);
        const nickNameToEntityMap = buildNickNameToEntityMap(lots);

        return {
            properties: enrichPropertiesWithEntityZip(
                properties,
                zipMap,
                lotToEntityMap,
                nickNameToEntityMap
            ),
            lookupLots: lots,
        };
    };

    const handleViewProperties = async (row) => {
        setLoader(true);
        try {
            const properties = await fetchPropertyDetails(row.pricingPlanName.trim());
            const { properties: enrichedProperties } =
                await enrichAssignedPropertiesForDisplay(properties);
            setSelectedRow({ ...row, properties: enrichedProperties });
            setOpenPropertiesPopup(true);
        } catch (error) {
            console.error("Failed to fetch properties", error);
        } finally {
            setLoader(false);
        }
    };

    const handleAssignPlan = async () => {
        if (!selectedRow) return;
        setAssignError("");

        if (assignType === "property" && selectedProperties.length > 0) {
            const assignedNames = alreadyAssignedProperties.map(p => String(p.nickName).toLowerCase());
            const assignedIds = alreadyAssignedProperties.map(p => p.parking_Lot_ID);
            const duplicates = selectedProperties.filter(
                p => assignedIds.includes(p.value) || assignedNames.includes(String(p.label).toLowerCase())
            );
            if (duplicates.length > 0) {
                setAssignError(
                    `Property "${duplicates.map(d => d.label).join('", "')}" is already assigned to this pricing plan.`
                );
                return;
            }
        }

        setLoader(true);

        try {
            if (assignType === "zip") {
                await assignPlanByZip();
            } else if (assignType === "property") {
                await assignPlanByProperty();
            }

            await fetchPricingPlans(); // Refresh
            setassignPropertiesPopup(false);
            setSelectedZipCodes([]);
            setSelectedProperties([]);
        } catch (error) {
            console.error("Assignment failed", error);
            setAssignError(error.message || "Failed to assign pricing plan");
        } finally {
            setLoader(false);
        }
    };

    const handleRemoveProperty = async (property) => {
        setRemovingProperty(true);
        setLoader(true);
        try {
            let lots = lookupLots;
            if (!lots.length) {
                const enriched = await enrichAssignedPropertiesForDisplay([property]);
                lots = enriched.lookupLots;
            }

            const resolvedProperty = resolvePropertyRecordForAssignment(property, lots);
            const parkingLotId = getParkingLotId(resolvedProperty);
            const nickName = String(
                resolvedProperty?.nickName ||
                resolvedProperty?.NickName ||
                property?.nickName ||
                ""
            ).trim();

            // Backend remove lookup is keyed by the ZIP stored on the assignment record
            // (from GetPropertyDetailsByPricingPlan), not the entity mailing ZIP shown in the UI.
            const zipCandidates = [
                ...new Set(
                    [
                        property?.zipcode,
                        property?.zipCode,
                        property?.Zipcode,
                        property?.ZipCode,
                        getPropertyZipCode(property),
                        getPropertyZipCode(resolvedProperty),
                        property?.displayZipCode,
                        resolvedProperty?.displayZipCode,
                    ]
                        .map((zip) => String(zip || "").trim())
                        .filter(Boolean)
                ),
            ];

            if (!parkingLotId && !nickName) {
                throw new Error("Unable to identify the selected property for removal.");
            }

            await removeAssignedProperty({
                pricingPlanId: selectedRow?.pricingPlanID,
                pricingPlanName: selectedRow?.pricingPlanName,
                parkingLotId,
                nickName,
                zipCandidates,
            });

            await fetchPricingPlans();
            const properties = await fetchPropertyDetails(selectedRow.pricingPlanName.trim());
            const { properties: enrichedProperties } =
                await enrichAssignedPropertiesForDisplay(properties);
            setSelectedRow(prev => ({ ...prev, properties: enrichedProperties }));
        } catch (err) {
            console.error("Remove failed", err);
            alert(err.message || "Failed to remove property");
        } finally {
            setRemovingProperty(false);
            setLoader(false);
        }
    };

    // ================= API CALLS =================

    const fetchPropertyDetails = async (pricingPlanName) => {
        try {
            const response = await fetch(`${hostPath}/api/GetPropertyDetailsByPricingPlan`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key": hostHeader
                },
                body: JSON.stringify({ pricingPlanName })
            });

            if (!response.ok) throw new Error("Failed to fetch property details");
            const data = await response.json();
            return data.properties || [];
        } catch (error) {
            console.error("Error fetching property details:", error);
            return [];
        }
    };

    const fetchZipCodes = async () => {
        try {
            const [zipResponse, activeLotsResponse, searchResponse, entityLotsData, ownersData] = await Promise.all([
                fetch(`${hostPath}/api/GetAllZipcodesAndNicknamesFromZipCodecontainer`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-functions-key": hostHeader
                    }
                }),
                fetch(`${hostPath}/api/GetAllParkingLots?status=Active`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-functions-key": hostHeader
                    }
                }),
                fetch(`${hostPath}/api/GetSearchProperty`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "x-functions-key": hostHeader
                    }
                }),
                fetchAllParkingLotsForPricingAssignment(),
                fetchUsersWithValidEmpId().catch(() => [])
            ]);

            const zipContainerData = zipResponse.ok ? await zipResponse.json() : [];
            const activeLotsData = activeLotsResponse.ok ? await activeLotsResponse.json() : [];
            const searchData = searchResponse.ok ? await searchResponse.json() : [];
            const owners = Array.isArray(ownersData) ? ownersData : [];
            const entityZipMap = buildEntityZipMap(owners);
            setEntityZipByEntityId(entityZipMap);

            // Only Active parking lots are pricing-plan eligible. Do not treat
            // ZIP-container / Submitted search rows as Active just because status is missing.
            const activeLots = flattenParkingLotRecords(activeLotsData).map((property) =>
                normalizePropertyForPricingAssignment(property, { defaultStatus: "Active" })
            );

            const searchLots = (Array.isArray(searchData) ? searchData : []).map((row) =>
                normalizePropertyForPricingAssignment({
                    parking_Lot_ID:
                        row.parking_Lot_ID ??
                        row.parkingLotID ??
                        row.parkingLotId ??
                        row.ParkingLotID,
                    nickName: row.nickName || row.NickName || row.parkingLotNickName,
                    parkingLotAddress: row.parkingLotMailingAdderess || row.parkingLotAddress,
                    city: row.city || row.City,
                    state: row.state || row.State,
                    zipcode: row.zipcode || row.zipCode || row.Zipcode || row.ZipCode,
                    status: row.status || row.Status,
                    entityRegistrationID: row.entityRegistrationID || row.EntityRegistrationID,
                })
            );

            const zipContainerLots = (Array.isArray(zipContainerData) ? zipContainerData : []).map((property) =>
                normalizePropertyForPricingAssignment(property)
            );

            const entityLots = (Array.isArray(entityLotsData) ? entityLotsData : []).map((property) =>
                normalizePropertyForPricingAssignment({
                    ...property,
                    entityRegistrationID:
                        property?.entityRegistrationID ??
                        property?.EntityRegistrationID ??
                        getEntityRegistrationId(property),
                })
            );

            const mergedLots = mergePropertyRecords(
                zipContainerLots,
                searchLots,
                entityLots,
                activeLots
            );

            const enrichedLots = mergedLots.map((property) => {
                const entityId = getEntityRegistrationId(property);
                return {
                    ...property,
                    entityZip: entityId ? entityZipMap[entityId] || "" : "",
                };
            });

            const assignableProperties = filterAssignableProperties(enrichedLots)
                .filter((property) => getParkingLotId(property));

            // ZIP dropdown must only list codes that have at least one Active property.
            // Submitted properties (e.g. 91754 / 33411) must not appear.
            const activeEntityIds = new Set(
                assignableProperties.map(getEntityRegistrationId).filter(Boolean).map(String)
            );
            const zipCodesFromActiveProperties = assignableProperties
                .flatMap((property) => [getPropertyZipCode(property), property.entityZip])
                .filter(Boolean);
            const zipCodesFromActiveEntities = owners
                .filter((owner) =>
                    activeEntityIds.has(String(owner.entityRegistrationID || owner.EntityRegistrationID || ""))
                )
                .map(getEntityAddressZipCode)
                .filter(Boolean);

            const normalizedProperties = assignableProperties.map(p => ({
                label: p.nickName || p.NickName,
                value: getParkingLotId(p),
                city: p.city || p.City,
                state: p.state || p.State,
                zipcode: getPropertyZipCode(p),
                status: getPropertyStatus(p)
            }));

            setRawProperties(assignableProperties);
            setAllZipCodes([
                ...new Set(
                    [...zipCodesFromActiveProperties, ...zipCodesFromActiveEntities]
                        .map((zip) => String(zip).trim())
                        .filter(Boolean)
                )
            ].sort());
            setAllProperties(normalizedProperties);
            setstates([...new Set(assignableProperties.map(item => item.state || item.State).filter(Boolean))].sort());
            setcities(assignableProperties);
        } catch (error) {
            console.error("Error fetching ZIP codes:", error);
            setAllZipCodes([]);
            setRawProperties([]);
            setAllProperties([]);
        }
    };

    const assignPlanByZip = async () => {
        if (!selectedZipCodes.length) {
            alert("Please select at least one ZIP code");
            return;
        }

        const zipCodes = selectedZipCodes.map(zip => String(zip).trim()).filter(Boolean);

        if (!zipCodes.length) {
            alert("Please select at least one ZIP code");
            return;
        }

        const selectedZipSet = new Set(zipCodes.map(zip => String(zip)));
        const matchingProperties = rawProperties.filter(
            (property) =>
                isPricingPlanAssignable(property) &&
                [...selectedZipSet].some((zip) => propertyMatchesZipCode(property, zip, entityZipByEntityId))
        );
        const parkingLotIds = matchingProperties
            .map(getParkingLotId)
            .filter(Boolean);

        if (!parkingLotIds.length) {
            throw new Error("No Active properties found for the selected ZIP code(s).");
        }

        await fetchJsonOrThrow(`${hostPath}/api/AssignPricingPlanWithParkingLots`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-functions-key": hostHeader
            },
            body: JSON.stringify({
                PricingPlanID: selectedRow.pricingPlanID,
                PricingPlanName: selectedRow.pricingPlanName,
                ParkingLotIds: parkingLotIds
            })
        }, "Failed to assign pricing plan by ZIP code");
    };

    const assignPlanByProperty = async () => {
        if (!selectedProperties.length) {
            alert("Please select at least one property");
            return;
        }

        const parkingLotIds = selectedProperties
            .map(p => Number(p.value))
            .filter(id => Number.isInteger(id) && id > 0);

        if (!parkingLotIds.length) {
            alert("Invalid parking lot selection");
            return;
        }

        const ineligibleSelections = selectedProperties.filter(
            (property) => !isPricingPlanAssignable(property)
        );
        if (ineligibleSelections.length > 0) {
            throw new Error(ValidationMessages.pricingPlanActiveOnly);
        }

        const payload = {
            PricingPlanID: selectedRow.pricingPlanID,
            PricingPlanName: selectedRow.pricingPlanName,
            ParkingLotIds: parkingLotIds
        };

        await fetchJsonOrThrow(`${hostPath}/api/AssignPricingPlanWithParkingLots`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-functions-key": hostHeader
            },
            body: JSON.stringify(payload)
        }, "Failed to assign pricing plan to properties");
    };

    const isDefaultPricingPlan = (plan = {}) =>
        Boolean(plan?.isDefault ?? plan?.IsDefault ?? plan?.is_default ?? plan?.Default);

    const findDefaultPricingPlan = (plans = rowsData) =>
        (Array.isArray(plans) ? plans : []).find(isDefaultPricingPlan) || null;

    const removeAssignedProperty = async ({
        pricingPlanId,
        pricingPlanName,
        parkingLotId,
        nickName,
        zipCandidates = [],
    }) => {
        const numericLotId = Number(parkingLotId);
        const validLotId =
            Number.isInteger(numericLotId) && numericLotId > 0 ? numericLotId : null;
        const normalizedNickName = nickName ? String(nickName).trim() : "";
        const zipOptions = [
            ...new Set(
                (Array.isArray(zipCandidates) ? zipCandidates : [])
                    .map((zip) => String(zip || "").trim())
                    .filter(Boolean)
            ),
        ];
        const requestHeaders = {
            "Content-Type": "application/json",
            "x-functions-key": hostHeader,
        };

        // Refresh plans so IsDefault / isDefault is current before deciding remove path.
        let latestPlans = rowsData;
        try {
            const plansResponse = await fetch(`${hostPath}/api/GetAllPricingPlans`, {
                method: "GET",
                headers: requestHeaders,
            });
            if (plansResponse.ok) {
                const plansData = await plansResponse.json();
                latestPlans = (Array.isArray(plansData) ? plansData : []).map((item) => ({
                    ...item,
                    isDefault: isDefaultPricingPlan(item),
                }));
                setRowsData((prev) =>
                    latestPlans.map((plan) => {
                        const existing = prev.find(
                            (row) =>
                                String(row.pricingPlanID) === String(plan.pricingPlanID)
                        );
                        return {
                            ...plan,
                            propertyCount: existing?.propertyCount ?? 0,
                        };
                    })
                );
            }
        } catch (error) {
            console.error("Failed to refresh pricing plans before remove", error);
        }

        const defaultPlan = findDefaultPricingPlan(latestPlans);
        const removingFromDefault =
            isDefaultPricingPlan(selectedRow) ||
            (defaultPlan?.pricingPlanID != null &&
                String(defaultPlan.pricingPlanID) === String(pricingPlanId || selectedRow?.pricingPlanID));
        let lastError;

        // Non-default plan: move property onto the Default plan (same as backend intent).
        if (!removingFromDefault && defaultPlan?.pricingPlanID && validLotId) {
            await fetchJsonOrThrow(
                `${hostPath}/api/AssignPricingPlanWithParkingLots`,
                {
                    method: "POST",
                    headers: requestHeaders,
                    body: JSON.stringify({
                        PricingPlanID: defaultPlan.pricingPlanID,
                        PricingPlanName: defaultPlan.pricingPlanName,
                        ParkingLotIds: [validLotId],
                    }),
                },
                "Failed to remove assigned pricing plan"
            );
            return;
        }

        // Default plan (or no move target): clear / unassign the property.
        // ResetPropertyToDefaultPlan cannot remove a property that is already on Default.
        if (validLotId) {
            const clearPayloads = [
                {
                    PricingPlanID: null,
                    PricingPlanName: null,
                    ParkingLotIds: [validLotId],
                },
                {
                    PricingPlanID: 0,
                    PricingPlanName: "",
                    ParkingLotIds: [validLotId],
                },
                {
                    PricingPlanID: "",
                    PricingPlanName: "Unassigned",
                    ParkingLotIds: [validLotId],
                },
            ];

            for (const payload of clearPayloads) {
                try {
                    await fetchJsonOrThrow(
                        `${hostPath}/api/AssignPricingPlanWithParkingLots`,
                        {
                            method: "POST",
                            headers: requestHeaders,
                            body: JSON.stringify(payload),
                        },
                        "Failed to remove assigned pricing plan"
                    );
                    return;
                } catch (error) {
                    lastError = error;
                }
            }
        }

        // Legacy reset API — works for non-default assignments; may also accept clear when already default.
        const attempts = [];
        const zipsToTry = zipOptions.length ? zipOptions : [""];

        zipsToTry.forEach((zip) => {
            const legacyPayload = {
                pricingPlanName,
                nickName: normalizedNickName,
                ...(zip ? { zipcode: zip } : {}),
            };
            attempts.push({ method: "PUT", payload: legacyPayload });
            attempts.push({ method: "POST", payload: legacyPayload });

            // Explicit unassign signal for default-plan removal.
            if (removingFromDefault) {
                const unassignPayload = {
                    ...legacyPayload,
                    Unassign: true,
                    unassign: true,
                    IsDefault: true,
                    isDefault: true,
                };
                attempts.push({ method: "PUT", payload: unassignPayload });
                attempts.push({ method: "POST", payload: unassignPayload });
            }
        });

        if (validLotId) {
            zipsToTry.forEach((zip) => {
                attempts.push({
                    method: "PUT",
                    payload: {
                        pricingPlanName,
                        nickName: normalizedNickName,
                        ...(zip ? { zipcode: zip } : {}),
                        ParkingLotIds: [validLotId],
                        parkingLotId: validLotId,
                        ...(pricingPlanId ? { PricingPlanID: pricingPlanId } : {}),
                        ...(removingFromDefault ? { Unassign: true, unassign: true } : {}),
                    },
                });
            });
        }

        for (const attempt of attempts) {
            try {
                await fetchJsonOrThrow(
                    `${hostPath}/api/ResetPropertyToDefaultPlan`,
                    {
                        method: attempt.method,
                        headers: requestHeaders,
                        body: JSON.stringify(attempt.payload),
                    },
                    "Failed to remove assigned pricing plan"
                );
                return;
            } catch (error) {
                lastError = error;
            }
        }

        if (!validLotId) {
            throw new Error(
                `Unable to find parking lot ID for "${normalizedNickName || "this property"}". Close this dialog, reopen Assigned Properties, and try again.`
            );
        }

        if (removingFromDefault) {
            throw new Error(
                lastError?.message ||
                    "Unable to remove a property from the Default plan. The backend cannot unassign from Default with the current APIs. Mark another plan as Default, then remove from Plan E, or add a backend Unassign API."
            );
        }

        if (!defaultPlan) {
            throw new Error(
                "Cannot remove this property because no default pricing plan is set. Open Pricing Settings, mark one plan as Default, then click Remove again."
            );
        }

        throw lastError || new Error("Failed to remove assigned pricing plan");
    };

    // ================= UTILITY FUNCTIONS =================

    const formatDays = (arr) =>
        weekOrder.filter(d => arr.includes(d)).map(d => dayMap[d]).join(", ");

    const formatTime = (timeString) => {
        const [hours, minutes] = timeString.split(":");
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes));
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getPriceRange = (rules) => {
        if (!rules || rules.length === 0) return "—";
        const prices = rules.map(r => Number(r.price));
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        return min === max ? `$${min}` : `$${min} - $${max}`;
    };

    // ================= EFFECTS =================

    useEffect(() => {
        if (!selectedState) {
            setFilteredCities([]);
            setSelectedCity("");
            return;
        }

        const citiesByState = [
            ...new Set(
                cities
                    .filter(c => c.state === selectedState)
                    .map(c => c.city)
                    .filter(Boolean)
            )
        ].sort();

        setFilteredCities(citiesByState);
    }, [selectedState, cities]);

    useEffect(() => {
        if (!selectedState && !selectedCity) {
            setFilteredProperties(allProperties);
            return;
        }

        let filtered = rawProperties;

        if (selectedState) {
            filtered = filtered.filter(p => p.state === selectedState);
        }

        if (selectedCity) {
            filtered = filtered.filter(p => p.city === selectedCity);
        }

        const normalizedFiltered = filtered
            .filter(isPricingPlanAssignable)
            .map(p => ({
            label: p.nickName || p.NickName,
            value: getParkingLotId(p),
            city: p.city || p.City,
            state: p.state || p.State,
            zipcode: getPropertyZipCode(p),
            status: getPropertyStatus(p)
        }));

        setFilteredProperties(normalizedFiltered);
    }, [selectedState, selectedCity, rawProperties, allProperties]);

    // ================= RENDER =================

    return (
        <>
                        <TableContainer
                component={Paper}
                className="pricing-table-container"
            >
                <Table  className="pricing-table">

                    <TableHead>
                        <TableRow>
                            <TableCell>Pricing Plan</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Pricing Rules</TableCell>
                            <TableCell>Price Range</TableCell>
                            <TableCell>Properties Assigned</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rowsData.length > 0 ? (
                            rowsData.map((row) => (
                                <TableRow key={row.pricingPlanID}>
                                    <TableCell>
                                        {row.pricingPlanName}
                                        {row.isDefault && <span className="default-badge">Default</span>}
                                    </TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>
                                        {`${row.rules?.length || 0} ${row.rules?.length === 1 ? "Rule" : "Rules"}`}
                                    </TableCell>
                                    <TableCell>{getPriceRange(row.rules)}</TableCell>
                                    <TableCell>
                                        <span
                                            style={{ textDecoration: "underline", cursor: "pointer" }}
                                            onClick={() => handleViewProperties(row)}
                                        >
                                            {row.propertyCount ?? 0} Properties
                                        </span>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 170 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "6px" }}>
                                            <span
                                                style={{
                                                    textDecoration: "underline",
                                                    color: "#1976d2",
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap"
                                                }}
                                                onClick={() => handleAssignClick(row)}
                                            >
                                                Assign Properties
                                            </span>

                                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </TableCell>

                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No Pricing Plans Found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ================= MENU ================= */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                    onClick={() => {
                        onEditPlan(selectedRowForMenu);
                        handleMenuClose();
                    }}
                >
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => handleDeletePlan(selectedRowForMenu)}
                    sx={{ color: "error.main" }}
                >
                    Delete
                </MenuItem>
            </Menu>

            {/* ================= DELETE DIALOG ================= */}

            <Dialog
                classes={{ paper: 'custom-paper-class-delete' }}
                aria-labelledby="customized-dialog-title"
                dividers
                open={openDeleteDialog}
                onClose={() => !deletingPlan && handleDeleteConfirm('cancel')}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                {deletingPlan && (
                    <div className="loader-overlay delete-plan-loader">
                        <FadeLoader />
                    </div>
                )}
                <DialogTitle
                    id="customized-dialog-title"
                    className="delete-dialog-title"
                >
                    Are You Sure You Want to Delete This Pricing Plan?
                </DialogTitle>

                <DialogContent className="delete-dialog-content">
                    <Typography gutterBottom>
                        Once deleted, this action is irreversible.
                        Please confirm deleting{" "}
                        <strong>{planToDelete?.pricingPlanName}</strong>.
                    </Typography>

                    {planToDelete?.propertyCount > 0 && (
                        <Typography
                            gutterBottom
                            className="delete-warning-box"
                        >
                            ⚠️ <strong>{planToDelete.propertyCount} {planToDelete.propertyCount === 1 ? "property is" : "properties are"}</strong> assigned to this plan.{" "}
                            {planToDelete?.isDefault
                                ? "Since this is the default plan, those properties will become unassigned."
                                : "They will be automatically moved to the default plan."}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions className="delete-dialog-actions">
                    <Button
                        className='custom-button-cancel'
                        disabled={deletingPlan}
                        onClick={() => handleDeleteConfirm('cancel')}
                    >
                        Cancel
                    </Button>

                    <Button
                        className='custom-button-delete'
                        disabled={deletingPlan}
                        onClick={() => handleDeleteConfirm('confirm')}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ================= VIEW PROPERTIES DIALOG ================= */}
            <Dialog
                open={openPropertiesPopup}
                onClose={() => !removingProperty && setOpenPropertiesPopup(false)}
                maxWidth="md"
                fullWidth
            >
                {removingProperty && (
                    <div className="loader-overlay assigned-properties-loader">
                        <FadeLoader />
                    </div>
                )}
                <DialogTitle className='assign-dialog-title-primary'>Assigned Properties</DialogTitle>
                <DialogContent sx={{ paddingBottom: 0 }}>
                    <Paper elevation={0} className="view-props-summary-paper">
                        <Typography  className="assign-plan-info-name" variant="subtitle2" fontWeight={600}>
                            {selectedRow?.pricingPlanName}
                        </Typography>
                      
                    </Paper>
                </DialogContent>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Property Name</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Zip Code</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedRow?.properties?.length > 0 ? (
                                selectedRow.properties.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.nickName}</TableCell>
                                        <TableCell>{p.parkingLotAddress}</TableCell>
                                        <TableCell>{p.displayZipCode || "—"}</TableCell>
                                        <TableCell>
                                            <span
                                                className={`remove-link${removingProperty ? " remove-link-disabled" : ""}`}
                                                onClick={() => !removingProperty && handleRemoveProperty(p)}
                                            >
                                                Remove
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No Properties Assigned
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions
                   className="view-props-dialog-actions"
                >
                    <Button
                        className="button-close"
                        disabled={removingProperty}
                        onClick={() => setOpenPropertiesPopup(false)}
                    >
                        Close
                    </Button>
                </DialogActions>

            </Dialog>

            {/* ================= ASSIGN PROPERTIES DIALOG ================= */}
            <Dialog open={assignPropertiesPopup} onClose={() => setassignPropertiesPopup(false)} PaperProps={{ sx: { width: 584, borderRadius: "10px",p: "24px" } }}>
                <DialogTitle sx={{ p: 0 }}>
                    <Typography className="assign-dialog-title-primary">Assign Properties</Typography>
                    <Typography className="assign-dialog-title-secondary" color="text.secondary">
                        Choose how you want to apply this pricing plan by ZIP code or by specific properties
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 0, pt: 2 }}>
                    <Typography className="assign-plan-label">Pricing Plan</Typography>
                    <Paper elevation={0} className="assign-plan-info-paper">
                        <Typography className="assign-plan-info-name">{selectedRow?.pricingPlanName}</Typography>
                       
                    </Paper>

                    <Typography  className="assign-how-label">How would you like to assign this plan?</Typography>
                    <RadioGroup
                        row
                        value={assignType}
                        onChange={(e) => {
                            const value = e.target.value;
                            setAssignType(value);
                            if (value !== "zip") setSelectedZipCodes([]);
                            if (value !== "property") {
                                setSelectedProperties([]);
                                setSelectedCity('');
                                setSelectedState('');
                            }
                        }}
                        sx={{ mb: 1 }}
                    >
                        <FormControlLabel value="zip" control={<Radio
                            sx={{
                                color: "#1976d2",
                                "&.Mui-checked": { color: "#1976d2" },
                            }}
                        />} label="Assign by ZIP Code" />
                        <FormControlLabel value="property" control={<Radio
                            sx={{
                                color: "#1976d2",
                                "&.Mui-checked": { color: "#1976d2" },
                            }}
                        />} label="Assign to Specific Properties" />
                    </RadioGroup>

                    {assignType === "zip" && (
                        <Box sx={{ width: 280, mt: 0.5 }}>
                            <Typography fontWeight={400} fontSize={16} mb={0.5}>Select ZIP Code</Typography>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect  // ← ADD THIS
                                options={[SELECT_ALL, ...allZipCodes]}
                                value={selectedZipCodes}
                                getOptionLabel={getOptionLabel}
                                isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
                                onChange={(event, newValue) => {
                                    const selectedValues = newValue
                                        .map(getOptionValue)
                                        .filter(value => value !== SELECT_ALL.value);

                                    if (newValue.some(option => getOptionValue(option) === SELECT_ALL.value)) {
                                        setSelectedZipCodes(selectedZipCodes.length === allZipCodes.length ? [] : allZipCodes);
                                    } else {
                                        setSelectedZipCodes(selectedValues);
                                    }
                                }}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={getOptionValue(option) === SELECT_ALL.value ? selectedZipCodes.length === allZipCodes.length : selected} />
                                        {getOptionLabel(option)}
                                    </li>
                                )}
                                renderInput={(params) => <TextField {...params} placeholder="Search by ZIP Code" size="small" />}
                                renderTags={(value) => {
                                    if (!value.length) return null;
                                    return (
                                        <>
                                            <Chip label={value[0]} size="small" />
                                            {value.length > 1 && <Chip label={`+${value.length - 1}`} size="small" />}
                                        </>
                                    );
                                }}
                            />
                        </Box>
                    )}

                    {assignType === "property" && (
                        <>
                            <Box className="property-filter-row">

                                <Typography
                                   className="property-filter-label"
                                >
                                    Filter By :
                                </Typography>

                                <Autocomplete options={states} value={selectedState} onChange={(e, val) => { setSelectedState(val); setSelectedCity(null); }} renderInput={(params) => <TextField {...params} placeholder="State" size="small" />} sx={{ minWidth: 150 }} />
                                <Autocomplete options={filteredCities} value={selectedCity} disabled={!selectedState} onChange={(e, val) => setSelectedCity(val)} renderInput={(params) => <TextField {...params} placeholder="City" size="small" />} sx={{ minWidth: 150 }} />
                            </Box>
                            <Box className="property-select-box">
                                <Typography className="property-select-label">Select Property</Typography>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect  // ← ADD THIS
                                    options={[SELECT_ALL, ...filteredProperties]}
                                    value={selectedProperties}
                                    getOptionLabel={getOptionLabel}
                                    isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
                                    onChange={(event, newValue) => {
                                        if (newValue.some(v => getOptionValue(v) === SELECT_ALL.value)) {
                                            setSelectedProperties(selectedProperties.length === filteredProperties.length ? [] : filteredProperties);
                                        } else {
                                            setSelectedProperties(newValue.filter(value => getOptionValue(value) !== SELECT_ALL.value));
                                        }
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox checked={getOptionValue(option) === SELECT_ALL.value ? selectedProperties.length === filteredProperties.length : selected} />
                                            {getOptionLabel(option)}
                                        </li>
                                    )}
                                    renderInput={(params) => <TextField {...params} placeholder="Search by Property" size="small" />}
                                    renderTags={(value) => {
                                        if (!value.length) return null;
                                        return (
                                            <>
                                                <Chip label={value[0].label} size="small" />
                                                {value.length > 1 && <Chip label={`+${value.length - 1}`} size="small" />}
                                            </>
                                        );
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>

                {assignError && (
                    <Typography color="error" sx={{ mt: 1, fontSize: 14 }}>
                        {assignError}
                    </Typography>
                )}

                <DialogActions
                    className="assign-dialog-actions" sx={{ p: 0, pt: 2 }}
                >
                    <Button
                        variant="outlined"
                        className="assign-cancel-btn"
                        onClick={() => setassignPropertiesPopup(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        className="assign-plan-btn"
                        onClick={handleAssignPlan}
                    >
                        Assign Plan
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PricingPlanList;